// src/pages/Farmer/FollowupList.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import farmerVisitService from '../../services/farmerVisit.service';
import farmerService from '../../services/farmer.service';
import tourService from '../../services/tour.service';
import Toast from '../../components/Toast/Toast';
import './FollowupList.css';

// ── Log Visit Modal ────────────────────────────────────────────────────────
const LogVisitModal = ({ farmer, onClose, onSubmit, isSubmitting }) => {
  const [visitType, setVisitType] = useState(farmer.suggestedVisitType || 'FOLLOWUP');
  const [reason,    setReason]    = useState('');
  const [latitude,  setLatitude]  = useState('');
  const [longitude, setLongitude] = useState('');
  const [accuracy,  setAccuracy]  = useState(null); // NEW — needed for geofence checks
  const [detecting, setDetecting] = useState(false);
  const [error,     setError]     = useState('');
  const [toast, setToast]         = useState(null);

  useEffect(() => { detectLocation(); }, []);

  const detectLocation = () => {
    if (!navigator.geolocation) {
      setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
      return;
    }

    setDetecting(true);
    setError('');
    setToast({ message: 'Detecting location…', type: 'info' });

    let bestReading = null;
    let settled = false;

    const finish = (position) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);

      const { latitude: lat, longitude: lng, accuracy: acc } = position.coords;
      setLatitude(lat.toFixed(8));
      setLongitude(lng.toFixed(8));
      setAccuracy(acc);
      setDetecting(false);

      setToast({ message: '📍 Location detected!', type: 'success' });
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
          bestReading = position;
        }
        if (position.coords.accuracy <= 15) {
          finish(position);
        }
      },
      (err) => {
        if (settled) return;
        settled = true;
        navigator.geolocation.clearWatch(watchId);
        setDetecting(false);
        const message =
          err.code === err.PERMISSION_DENIED
            ? 'Location access denied. Please enable it in your browser/app settings.'
            : err.code === err.TIMEOUT
            ? 'Location request timed out. Please try again.'
            : 'Unable to detect location. ';
        setToast({ message, type: 'error' });
        console.error('Geolocation error:', err);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );

    setTimeout(() => {
      if (!settled && bestReading) {
        finish(bestReading);
      } else if (!settled) {
        settled = true;
        navigator.geolocation.clearWatch(watchId);
        setDetecting(false);
        setToast({ message: 'Could not get an accurate location. ', type: 'error' });
      }
    }, 12000);
  };

  const handleSubmit = () => {
    if (!latitude || !longitude) { setError('Your location is required'); return; }
    if (visitType === 'FOLLOWUP' && !reason.trim()) {
      setError('Please write a short reason for the followup');
      return;
    }
    onSubmit({
      visitType,
      reason:         visitType === 'FOLLOWUP' ? reason.trim() : undefined,
      visitLatitude:  latitude,
      visitLongitude: longitude,
      locationAccuracy: accuracy, // pass this through so backend can store + validate against it
    });
  };

  return (
    <div className="lvm-overlay" onClick={onClose}>
      <div className="lvm-modal" onClick={e => e.stopPropagation()}>
        {/* Mobile drag handle */}
        <div className="lvm-handle" />

        {/* Header */}
        <div className="lvm-header">
          <h5 className="lvm-title">{farmer.farmerName}</h5>
          <p className="lvm-sub">
            📱 {farmer.mobileNo} · 🏘️ {farmer.village || '—'}
          </p>
        </div>

        <div className="lvm-body">
          {/* Visit Type Toggle */}
          <div className="lvm-field">
            <label className="lvm-label">Visit Type</label>
            <div className="lvm-toggle">
              <button type="button"
                className={`lvm-toggle-btn ${visitType === 'FIRST_VISIT' ? 'active' : ''}`}
                onClick={() => { setVisitType('FIRST_VISIT'); setError(''); }}>
                🆕 First Visit
              </button>
              <button type="button"
                className={`lvm-toggle-btn ${visitType === 'FOLLOWUP' ? 'active' : ''}`}
                onClick={() => { setVisitType('FOLLOWUP'); setError(''); }}>
                🔁 Followup
              </button>
            </div>
            {farmer.visitCount > 0 && (
              <p className="lvm-hint">
                {farmer.visitCount} previous visit{farmer.visitCount === 1 ? '' : 's'} recorded
              </p>
            )}
          </div>

          {/* Location */}
          <div className="lvm-field">
            <label className="lvm-label">Your Location</label>
            {detecting ? (
              <div className="lvm-loc-detecting">📍 Getting your location...</div>
            ) : latitude ? (
              <div className="lvm-loc-ok">
                <span>✅ Location detected</span>
                <button type="button" onClick={detectLocation} className="lvm-loc-refresh">
                  Refresh
                </button>
              </div>
            ) : (
              <button type="button" onClick={detectLocation} className="lvm-loc-detect-btn">
                📍 Detect My Location
              </button>
            )}
          </div>

          {/* Reason — only for followup */}
          {visitType === 'FOLLOWUP' && (
            <div className="lvm-field">
              <label className="lvm-label">
                What did you discuss? <span className="lvm-required">*</span>
              </label>
              <textarea
                value={reason}
                onChange={e => { setReason(e.target.value); setError(''); }}
                placeholder="e.g. Discussed crop spray, took order, fertilizer enquiry..."
                rows={3}
                className="lvm-textarea"
              />
            </div>
          )}

          {visitType === 'FIRST_VISIT' && (
            <div className="lvm-info-card">
              First-time visit. Your current location will be saved as the farmer's location.
            </div>
          )}

          {error && <p className="lvm-error">⚠ {error}</p>}
        </div>

        {/* Footer buttons */}
        <div className="lvm-footer">
          <button type="button" onClick={onClose} disabled={isSubmitting}
            className="lvm-btn-cancel">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit}
            disabled={isSubmitting || detecting || !latitude}
            className="lvm-btn-submit">
            {isSubmitting ? 'Submitting...' : '✓ Submit Visit'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────
const FollowupList = () => {
  const navigate = useNavigate();

  const [todayTour, setTodayTour] = useState(null);
  const [tourLoading, setTourLoading] = useState(true);

  const [farmers, setFarmers] = useState([]);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(true);

  const [modalFarmer,  setModalFarmer]  = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [toast, setToast] = useState(null);

  const tourActive = todayTour && todayTour.status !== 'COMPLETED';

  useEffect(() => {
    tourService.getTodayTour()
      .then(res => { if (res.success && res.data) setTodayTour(res.data); })
      .catch(() => {})
      .finally(() => setTourLoading(false));
  }, []);

  useEffect(() => {
    setLoading(true);
    farmerService.getAllFarmers(1, 1000, '')
      .then(res => {
        if (res.success) setFarmers(res.data?.farmers || []);
      })
      .catch(() => setToast({ message: 'Failed to load farmers', type: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const handleLogVisit = async (data) => {
    if (!modalFarmer) return;
    setIsSubmitting(true);
    try {
      const res = await farmerVisitService.createVisit({
        farmerId: modalFarmer.id,
        ...data,
      });
      if (res.success) {
        setToast({ message: '✅ Visit logged!', type: 'success' });
        setModalFarmer(null);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to log visit',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filtered = farmers.filter(f => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      f.farmerName?.toLowerCase().includes(s) ||
      f.mobileNo?.includes(s) ||
      f.villageRef?.name?.toLowerCase().includes(s) ||
      f.village?.toLowerCase().includes(s)
    );
  });

  if (tourLoading) {
    return (
      <div className="fv-page">
        <div className="fv-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="fv-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="fv-header">
        <h4 className="fv-title">Farmer Visit</h4>
        <p className="fv-subtitle">Find a farmer and log your visit</p>
      </div>

      {/* Tour banner */}
      {!todayTour ? (
        <div className="fv-banner fv-banner-warn">
          <span>⚠️ No tour started today</span>
          <button onClick={() => navigate('/tour/start-km')} className="fv-banner-btn">
            Start Tour
          </button>
        </div>
      ) : todayTour.status === 'COMPLETED' ? (
        <div className="fv-banner fv-banner-danger">
          ⛔ Tour completed for today
        </div>
      ) : (
        <div className="fv-banner fv-banner-ok">
          ✅ Tour active · Start KM: <strong>{todayTour.startKm}</strong>
        </div>
      )}

      {/* Search */}
      <div className="fv-search">
        <span className="fv-search-icon">🔍</span>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by farmer name, mobile, or village..."
          className="fv-search-input"
        />
      </div>

      <p className="fv-count">
        {loading ? 'Loading...' : `${filtered.length} farmer${filtered.length === 1 ? '' : 's'}`}
      </p>

      {/* Farmer list */}
      {loading ? (
        <div className="fv-loading">Loading farmers...</div>
      ) : filtered.length === 0 ? (
        <div className="fv-empty">
          {search ? `No farmers match "${search}"` : 'No farmers found'}
        </div>
      ) : (
        <div className="fv-list">
          {filtered.map(farmer => (
            <div key={farmer.id} className="fv-card">
              <div className="fv-card-info">
                <p className="fv-card-name">{farmer.farmerName}</p>
                <p className="fv-card-meta">📱 {farmer.mobileNo}</p>
                <p className="fv-card-meta">🏘️ {farmer.villageRef?.name || farmer.village || '—'}</p>
              </div>

              {tourActive ? (
                <button
                  onClick={() => setModalFarmer({
                    ...farmer,
                    village: farmer.villageRef?.name || farmer.village || '',
                  })}
                  className="fv-card-btn"
                >
                  + Log Visit
                </button>
              ) : (
                <span className="fv-card-disabled">Start tour to log</span>
              )}
            </div>
          ))}
        </div>
      )}

      {modalFarmer && (
        <LogVisitModal
          farmer={modalFarmer}
          onClose={() => setModalFarmer(null)}
          onSubmit={handleLogVisit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default FollowupList;